import DashboardIcon from '@mui/icons-material/Dashboard';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import {
  Box,
  ButtonGroup,
  Chip,
  Grid,
  Grow,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { ReactNode, useState } from 'react';
import useWindowDimensions from '../../../../infrastructure/utils/hooks/useDimensions';
import BarChartsAvgMonthsCredits from './barChartsAvgMonthsCredits';
import BarChartsCapitalPerYear from './barChartsCapitalPerYear';
import BarChartsDataCities from './barChartsCities';
import BarChartsCreditsPerYear from './barChartsCreditsPerYear';
import HorizontalBarChartCompanies from './HorizontalBarChartCompanies';
import PieChartsEconomyActivities from './pieChartsEconomyActivities';
import PieChartsData from './pieChartsGenders';
import { TDashBoardPortfolioByAge } from './types.d';

type ViewColumns = 1 | 2 | 3;

type ChartCardProps = {
  title: string;
  timeout: number;
  columns: ViewColumns;
  heightSm: number;
  heightMd: number;
  children: ReactNode;
};

const gridSizeMap: Record<ViewColumns, number> = { 1: 12, 2: 6, 3: 4 };

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  timeout,
  columns,
  heightSm,
  heightMd,
  children,
}) => {
  const { h } = useWindowDimensions();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const gridSize = gridSizeMap[columns];

  const isCompact = columns === 3;

  return (
    <Grid
      item
      xl={gridSize}
      lg={gridSize}
      md={columns === 3 ? 6 : gridSize}
      xs={12}
      sx={{ padding: isCompact ? '5px' : '8px' }}>
      <Grow in timeout={timeout}>
        <Paper
          elevation={3}
          sx={{
            p: isCompact ? 0.75 : 1,
            borderRadius: isCompact ? 1.5 : 2,
            overflow: 'hidden',
            ':hover': { transform: 'scale(1.02)', transition: 'transform 300ms ease' },
          }}>
          <Box
            sx={{
              width: '100%',
              height: isSm ? h(heightSm) : h(isCompact ? heightMd * 0.9 : heightMd),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: isCompact ? 0.5 : 1,
            }}>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: isCompact ? '0.9em' : '1.1em',
                textAlign: 'center',
                color: theme.palette.primary.main,
                mb: 0.5,
                lineHeight: 1.2,
                flexShrink: 0,
              }}>
              {title}
            </Typography>
            {children}
          </Box>
        </Paper>
      </Grow>
    </Grid>
  );
};

export const DashBoardPortfolioByAgeV: React.FC<TDashBoardPortfolioByAge> = ({
  portfolioGenders,
  portfolioEconomicAct,
  portfolioCities,
  portfolioCapitalPerYear,
  portfolioCreditsPerYear,
  portfolioCompanies,
  portfolioAvgMonths,
  bgColors,
}) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [columns, setColumns] = useState<ViewColumns>(3);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: isSm ? '8px 10px' : '15px 20px 10px',
        width: '100%',
        height: '100%',
        overflow: 'auto',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          marginBottom: '8px',
          color: theme.palette.primary.dark,
        }}>
        <Typography sx={{ fontWeight: '700', fontSize: isSm ? '1rem' : '1.2rem' }}>
          Gráficos de cartera por edades
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isSm && (
            <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
              <Chip label={`Géneros: ${portfolioGenders?.length ?? 0}`} size="small" />
              <Chip label={`Actividades: ${portfolioEconomicAct?.length ?? 0}`} size="small" />
              <Chip label={`Ciudades: ${portfolioCities?.length ?? 0}`} size="small" />
              <Chip label={`Empresas: ${portfolioCompanies?.length ?? 0}`} size="small" />
            </Stack>
          )}
          <ButtonGroup size="small" variant="contained">
            <Tooltip title="3 columnas">
              <IconButton
                color={columns === 3 ? 'primary' : 'default'}
                aria-label="3 columnas"
                onClick={() => setColumns(3)}>
                <DashboardIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="2 columnas">
              <IconButton
                color={columns === 2 ? 'primary' : 'default'}
                aria-label="2 columnas"
                onClick={() => setColumns(2)}>
                <ViewModuleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="1 columna">
              <IconButton
                color={columns === 1 ? 'primary' : 'default'}
                aria-label="1 columna"
                onClick={() => setColumns(1)}>
                <ViewListIcon />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </Box>
      <Box sx={{ width: '100%', borderRadius: '5px', py: 1 }}>
        <Grid container>
          <ChartCard
            title="Créditos por año"
            timeout={400}
            columns={columns}
            heightSm={35}
            heightMd={30}>
            <BarChartsCreditsPerYear portfolioCreditsPerYear={portfolioCreditsPerYear} />
          </ChartCard>

          <ChartCard
            title="Cartera por año"
            timeout={500}
            columns={columns}
            heightSm={35}
            heightMd={30}>
            <BarChartsCapitalPerYear portfolioCapitalPerYear={portfolioCapitalPerYear} />
          </ChartCard>

          {portfolioEconomicAct?.length > 0 && (
            <ChartCard
              title="Clientes por actividad económica"
              timeout={600}
              columns={columns}
              heightSm={45}
              heightMd={30}>
              <PieChartsEconomyActivities
                portfolioEconomicAct={portfolioEconomicAct}
                bgColors={bgColors}
              />
            </ChartCard>
          )}

          <ChartCard
            title="Clientes por género"
            timeout={700}
            columns={columns}
            heightSm={35}
            heightMd={30}>
            <PieChartsData portfolioGenders={portfolioGenders} bgColors={bgColors} />
          </ChartCard>

          <ChartCard
            title="Plazo promedio por año"
            timeout={800}
            columns={columns}
            heightSm={35}
            heightMd={30}>
            <BarChartsAvgMonthsCredits portfolioAvgMonths={portfolioAvgMonths} />
          </ChartCard>

          <ChartCard
            title="Cartera por ciudad de residencia"
            timeout={900}
            columns={columns}
            heightSm={35}
            heightMd={30}>
            <BarChartsDataCities portfolioCities={portfolioCities} />
          </ChartCard>

          <ChartCard
            title="Cartera por pagaduría"
            timeout={1000}
            columns={columns}
            heightSm={35}
            heightMd={30}>
            <HorizontalBarChartCompanies portfolioCompanies={portfolioCompanies} />
          </ChartCard>
        </Grid>
      </Box>
    </Box>
  );
};
